/** @jsx jsx */
// External modules
import { Fragment, useEffect } from 'react';
import { jsx } from '@emotion/core';
import { bool, string } from 'prop-types';
import { useStoreActions, useStoreState } from '@/store';
import Loadable from 'react-loadable';
import { LoadingSpinner } from '@checkout/react-components';

// Internal modules
import {
  buildMPPUrl,
  features,
  getContent,
  getGlobals,
  getLazyLoaderRenderFunction,
  getLocale,
  isUserActionCommit,
  lazyLoader,
  logger,
} from '@/lib';
import {
  AddressLoadingContainer,
  CobrandedDisclaimerContainer,
  CTADisclaimerMessage,
  OvercaptureDisclaimerMessage,
  SignupValueProp,
  SignupValuePropBold,
  StreamlineSectionTitle,
} from './StyledComponents';
import {
  logOvercapturesWhenRendering,
  logSilentMemberMatchDisclaimerShown,
} from './fpti';
import { appStore } from '@/store';
import { onboardAccount } from './handlers';
import { InfoMessage } from '../Common/PageMessages';
import { addressHeaderStyles } from '../Address/styles';
import { SectionHeading, SectionHeadingLarge } from '../Headings';
import { Link, LoadingFields, Spots } from '@/components/Common';
import { ExpandedValueProps } from '@/components/CheckoutIncentives/ExpandedValueProps';

if (features.isNotActive('isAddressless')) {
  logger.fptiTrackPageLoad.registerItem('Address');
}

const AccountSectionHeader = () => {
  const isOptionalSignup = features.isActive('isOptionalSignup');
  const flow = isOptionalSignup ? 'guest' : 'signup';

  /**
   * Adding `isOptionalSignup` to this check for the time being since
   * address-less is on in sign up flows (IN). If we expand to guest
   * eligible countries we should remove this if the design is updated
   * for guest flows.
   */
  if (!isOptionalSignup && features.isActive('isAddressless')) {
    return (
      <SectionHeading>
        {getContent(`sectionHeaders.accountInfo.${flow}.addressless`)}
      </SectionHeading>
    );
  }

  return (
    <Fragment>
      <SectionHeadingLarge data-testid="accountSectionHeader">
        {getContent(`sectionHeaders.accountInfo.${flow}.base`, {
          brTag: <br />,
        })}
      </SectionHeadingLarge>

      <SignupValueProp>
        {getContent(
          `valueProps.accountInfo.${flow}.base`,
          getValuePropComponents(),
        )}
      </SignupValueProp>

      {isOptionalSignup && (
        <SignupValuePropBold>
          {getContent(`sectionHeaders.accountInfo.${flow}.subheader`)}
        </SignupValuePropBold>
      )}
    </Fragment>
  );
};

const StreamlineSectionHeader = ({
  enableSilentDisclaimer = false,
  showHeader = true,
}) => {
  const isExistingAccount = useStoreState(({ api }) => api.isExistingAccount);
  const isIsrael = getLocale()?.country === 'IL';

  // Treatment for Silent Member Match Disclaimer
  const isMemberMatchNoOptIn = features.isActive('isMemberMatchNoOptIn');
  // If in above treatment, and we've detected an existing account,
  // render silent disclaimer
  const isSilentMemberDisclaimer = isMemberMatchNoOptIn && isExistingAccount;

  if (enableSilentDisclaimer && isSilentMemberDisclaimer) {
    logger.info('rendered_silent_member_disclaimer');
    return SilentMemberMatchDisclaimer({
      testId: 'member-match-disclaimer-forced',
    });
  }
  const expandedValueProps = features.isActive('isExpandedValueProps');
  const isSignupOptional = features.isActive('isOptionalSignup');

  const ineligibleStretch = features.isActive(
    'isExpandedStretchRewardsIneligibleDownstream',
  );

  /* ExpandedValueProps already rendered in StreamlineOnboardOptions for signup optional.
     This is how we render it for forced signup. Both use StreamlineSectionHeader.
     !isSignupOptional prevents a double render. */
  if (expandedValueProps && !isSignupOptional && !ineligibleStretch) {
    return <ExpandedValueProps />;
  } else if (expandedValueProps && !ineligibleStretch) {
    /* We don't want to render any of this if in treatment for expanded value props
       and xobuyer has flagged this as eligible for stretch rewards. */
    return null;
  }

  const spots = [
    {
      content: getContent('valueProps.memberBenefits.shopWithConfidenceLabel'),
      iconName: 'SHIELD_LOCK',
    },
    {
      content: getContent(
        'valueProps.memberBenefits.shopMillionsOfMerchantsLabel',
      ),
      iconName: 'BAG_HANDLE',
    },
    !isIsrael && {
      content: getContent(
        'valueProps.memberBenefits.saveBigWithGreatDealsLabel',
      ),
      iconName: 'STAMP_PERCENT',
    },
  ];
  return (
    <Fragment>
      {showHeader && (
        <StreamlineSectionTitle>
          {getContent('simplifiedSectionHeaders.createAccount.label')}
        </StreamlineSectionTitle>
      )}
      <Spots spotCardContainerWidth="75%" spotLabelWidth="75%" spots={spots} />
    </Fragment>
  );
};

StreamlineSectionHeader.propTypes = {
  enableSilentDisclaimer: bool,
  showHeader: bool,
};

const SilentMemberMatchDisclaimer = ({ testId }) => {
  return (
    <InfoMessage
      message={getContent(
        'simplifiedSectionHeaders.streamline.memberMatchGeneric',
      )}
      logOnMount={logSilentMemberMatchDisclaimerShown}
      testId={testId}
      withoutIcon
    />
  );
};

SilentMemberMatchDisclaimer.propTypes = {
  testId: string.isRequired,
};

const MppLink = ({ contentKey, path }) => {
  return (
    <Link href={buildMPPUrl({ mppPath: path })} text={getContent(contentKey)} />
  );
};

MppLink.propTypes = {
  contentKey: string.isRequired,
  path: string.isRequired,
};

/**
 * Please name content keys for value props based on:
 * (a) The name of the link if they are a link
 * (b) The name of the content if is bolded content
 *
 * Also, please make sure any new content key is first added to the
 * `Base/en/weasley/valueProps.properties` file. This will prevent any
 * content missing logs for countries that don't use that content key.
 * Generally we like to avoid adding content to countries that don't use it
 * in order to prevent bloat of content files, but this is an exception that
 * is necessary to prevent noise in logs we use for finding missing content.
 */
const getValuePropComponents = () => {
  const flow = features.isActive('isOptionalSignup') ? 'guest' : 'signup';
  return {
    buyerProtection: (
      <span css={{ fontWeight: '600' }}>
        {getContent(`valueProps.buyerProtection.${flow}`)}
      </span>
    ),
    buyerProtectionLink: (
      <MppLink
        contentKey={`valueProps.buyerProtectionLink.${flow}`}
        path="paypal-buyer-protection"
      />
    ),
    onlineShoppingLink: (
      <MppLink
        contentKey={`valueProps.onlineShoppingLink.${flow}`}
        path="online-shopping"
      />
    ),
    safetyAndSecurityLink: (
      <MppLink
        contentKey={`valueProps.safetyAndSecurityLink.${flow}`}
        path="paypal-safety-and-security"
      />
    ),
  };
};

const LazyAddress = lazyLoader({
  /*
    DO NOT ALTER 'webpackChunkName: 'address' COMMENT
    Adding this comment will cause our separate chunk
    to be named [my-chunk-name].{HASH}.js instead of [id].js.
    WHY? -  custom preload
  */
  loader: () => import(/* webpackChunkName: 'address' */ '../Address'),
  loading: () => {
    return (
      <AddressLoadingContainer>
        <p css={addressHeaderStyles}>{getContent('billing.billingAddress')}</p>
        <LoadingFields numLoaders={6} />
      </AddressLoadingContainer>
    );
  },
  name: 'address_input',
  render: getLazyLoaderRenderFunction({ componentName: 'Address' }),
});

const getReturnAfterSubmitMessage = amount => {
  const isExistingAccount = appStore.getState().api.isExistingAccount;
  logger.fpti('weasley_display_cpa_disclaimer', {
    amt: amount,
    isExistingAccount: `${isExistingAccount}`,
    msg_id: 'disclaimer.cardPreAuthorization.cartAbandoment.TREATMENT_2',
    state_name: 'billing',
    transition_name: 'process_billing_info',
  });
  return getContent(
    'disclaimer.cardPreAuthorization.cartAbandoment.TREATMENT_2',
    { amount: amount },
  );
};

// keeping the component as is for future CPA disclaimer message experiments
const CPADisclaimerMessage = () => {
  const cartDetails = useStoreState(({ api }) => api.cartDetails);
  const selectedOption = useStoreState(
    ({ form }) => form.currencyConversionSelection,
  );
  const conversionData = useStoreState(({ api }) => api.currencyConversionData);
  const total =
    (selectedOption === 'PAYPAL' && conversionData?.from) ||
    cartDetails?.amounts?.total;
  const disclaimerMsg = getReturnAfterSubmitMessage(
    total?.currencyFormatSymbolISOCurrency,
  );

  return (
    <CTADisclaimerMessage data-testid="CPADisclaimer">
      {disclaimerMsg}
    </CTADisclaimerMessage>
  );
};

const CobrandedDisclaimer = () => {
  return (
    <CobrandedDisclaimerContainer data-testid="CobrandedDisclaimer">
      {getContent('disclaimer.cobranded')}
    </CobrandedDisclaimerContainer>
  );
};

const OvercaptureDisclaimer = ({ amount }) => {
  const overcaptureContentKey = 'disclaimer.overcaptureExtended';

  return (
    <OvercaptureDisclaimerMessage data-testid="overcapture-disclaimer">
      {getContent(overcaptureContentKey, { amount })}
    </OvercaptureDisclaimerMessage>
  );
};

OvercaptureDisclaimer.propTypes = {
  amount: string.isRequired,
};

const DisclosureArea = () => {
  const showOvercatureInCommit = features.isActive(
    'shouldShowOvercaptureDisclaimerInCommit',
  );

  const isCPAEligible = features.config.CTAMessageTreatment === 'TREATMENT_2';
  const { checkoutSession } = getGlobals().initialGqlData;

  const cardCobrand = useStoreState(({ form }) => form.cardNumber.cobrand);
  const merchantCobrands = checkoutSession?.merchant?.cobrands || [];
  const isMerchantCobrandEligible = merchantCobrands.includes(cardCobrand);

  const overcaptureAmount =
    checkoutSession?.cart?.totalAllowedOverCaptureAmount
      ?.currencyFormatSymbolISOCurrency;

  const shouldDisplayOvercaptures = isUserActionCommit()
    ? overcaptureAmount && showOvercatureInCommit
    : overcaptureAmount;

  const setOverCaptureVisible = useStoreActions(
    ({ ui }) => ui.setOverCaptureVisible,
  );

  useEffect(() => {
    logOvercapturesWhenRendering(overcaptureAmount, shouldDisplayOvercaptures);
    setOverCaptureVisible(shouldDisplayOvercaptures);
  }, []);

  return (
    <Fragment>
      {isMerchantCobrandEligible && <CobrandedDisclaimer />}
      {shouldDisplayOvercaptures && (
        <OvercaptureDisclaimer amount={overcaptureAmount} />
      )}
      {isCPAEligible && !shouldDisplayOvercaptures && <CPADisclaimerMessage />}
    </Fragment>
  );
};

const LazyAddressNormalizedAddressInterstitial = Loadable({
  loader: () => import('../Interstitials/AddressNormalizationInterstitial'),
  loading: props => {
    const { ui: uiActions } = appStore.getActions();
    if (props.error) {
      logger.warn('address_normalization_bundle_fetch_failed');
      uiActions.toggleIsAddressNormalizeOpen(false);
      onboardAccount();
    }
    return null;
  },
  render(loaded, props) {
    const Component = loaded.AddressNormalizationInterstitial;
    return <Component />;
  },
});

const LazyInstallmentInterstitial = Loadable({
  loader: () => import('@/components/Interstitials/InstallmentInterstitial'),
  loading: () => <LoadingSpinner />,
  render(loaded, props) {
    const Component = loaded.InstallmentInterstitial;
    return <Component {...props} />;
  },
});

const LazyThreeDsInterstitial = lazyLoader({
  loader: () => import('@/components/Interstitials/ThreeDsInterstitial'),
  loading: () => (
    <LoadingSpinner message={getContent('spinner.contactingCardIssuer')} />
  ),
  name: 'three_ds_interstitial',
  render(loaded, props) {
    const Component = loaded.ThreeDsInterstitial;
    return <Component />;
  },
});

const LazyThreeDS2Interstitial = lazyLoader({
  loader: () => import('@/components/Interstitials/ThreeDS2Interstitial'),
  loading: () => (
    <LoadingSpinner message={getContent('spinner.contactingCardIssuer')} />
  ),
  name: 'three_ds_2_interstitial',
  render(loaded, props) {
    const Component = loaded.ThreeDS2Interstitial;
    return <Component />;
  },
});

const LazySepaInterstitial = lazyLoader({
  loader: () => import('@/components/Interstitials/SepaInterstitial'),
  loading: () => <LoadingSpinner />,
  name: 'sepa_interstitial',
  render(loaded, props) {
    const Component = loaded.SepaInterstitial;
    return <Component />;
  },
});

const LazyPhoneConfirmationInterstitial = lazyLoader({
  loader: () =>
    import('@/components/Interstitials/PhoneConfirmationInterstitial'),
  loading: () => <LoadingSpinner />,
  name: 'phone_confirmation_interstitial',
  render(loaded, props) {
    const Component = loaded.PhoneConfirmationInterstitial;
    return <Component />;
  },
});

const LazyOneTimePasswordConfirmationInterstitial = lazyLoader({
  loader: () =>
    import(
      '@/components/Interstitials/OneTimePasswordConfirmationInterstitial'
    ),
  loading: () => <LoadingSpinner />,
  name: 'one_time_password_confirmation_interstitial',
  render(loaded, props) {
    const Component = loaded.OneTimePasswordConfirmationInterstitial;
    return <Component />;
  },
});

const LazyUpgradeInterstitial = lazyLoader({
  loader: () =>
    import(
      '@/components/Interstitials/UpgradeInterstitial/WithPassword/UpgradeInterstitial'
    ),
  loading: () => <LoadingSpinner />,
  name: 'upgrade_interstitial',
  render(loaded, props) {
    const Component = loaded.UpgradeInterstitial;
    return <Component {...props} />;
  },
});

const LazyShipping = lazyLoader({
  loader: () => import('../Shipping'),
  loading: () => <LoadingSpinner />,
  name: 'shipping_methods',
  render(loaded, props) {
    const Component = loaded.Shipping;
    return <Component />;
  },
});

const LazyCRSInterstitial = lazyLoader({
  loader: () => import('@/components/Interstitials/CRSInterstitial'),
  loading: () => null,
  name: 'crs_interstitial',
  render(loaded, props) {
    const Component = loaded.CRSInterstitial;
    return <Component />;
  },
});

const LazyMemberBenefitsInterstitial = lazyLoader({
  loader: () => import('@/components/Interstitials/MemberBenefitsInterstitial'),
  loading: () => <LoadingSpinner />,
  name: 'member_default_interstitial',
  render(loaded, props) {
    const Component = loaded.MemberBenefitsInterstitial;
    return <Component {...props} />;
  },
});

const LazyMemberBenefitsTrueGuestsInterstitial = lazyLoader({
  loader: () =>
    import('@/components/Interstitials/MemberBenefitsTrueGuestsInterstitial'),
  loading: () => <LoadingSpinner />,
  name: 'member_true_guest_interstitial',
  render(loaded, props) {
    const Component = loaded.MemberBenefitsTrueGuestsInterstitial;
    return <Component {...props} />;
  },
});

const LazyPasswordlessUpgradeInterstitial = lazyLoader({
  loader: () =>
    import(
      '@/components/Interstitials/UpgradeInterstitial/Passwordless/PasswordlessUpgradeInterstitial'
    ),
  loading: () => <LoadingSpinner />,
  name: 'passwordless_upgrade_half_sheet_interstitial',
  render(loaded, props) {
    const Component = loaded.PasswordlessUpgradeInterstitial;
    return <Component {...props} />;
  },
});

export {
  AccountSectionHeader,
  CobrandedDisclaimer,
  CPADisclaimerMessage,
  OvercaptureDisclaimer,
  DisclosureArea,
  getValuePropComponents,
  LazyAddress,
  LazyAddressNormalizedAddressInterstitial,
  LazyInstallmentInterstitial,
  LazyOneTimePasswordConfirmationInterstitial,
  LazyPhoneConfirmationInterstitial,
  LazySepaInterstitial,
  LazyShipping,
  LazyThreeDsInterstitial,
  LazyThreeDS2Interstitial,
  LazyUpgradeInterstitial,
  LazyPasswordlessUpgradeInterstitial,
  LazyCRSInterstitial,
  LazyMemberBenefitsInterstitial,
  LazyMemberBenefitsTrueGuestsInterstitial,
  SilentMemberMatchDisclaimer,
  StreamlineSectionHeader,
};
